const TARGET_MAX_RESOLUTION = 1080;

export interface VideoCompressionResult {
  uri: string;
  size: number;
  duration: number;
  mimeType: string;
}

export interface CompressionProgress {
  stage: 'loading' | 'compressing' | 'finalizing';
  progress: number;
}

type ProgressCallback = (progress: CompressionProgress) => void;

// FFmpeg instance loaded from CDN
let ffmpeg: any = null;
let ffmpegLoaded = false;
let loadPromise: Promise<any> | null = null;

/**
 * Load FFmpeg from CDN (avoids Metro bundler MIME type issues)
 */
async function loadFFmpeg(onProgress?: ProgressCallback): Promise<any> {
  if (ffmpeg && ffmpegLoaded) {
    return ffmpeg;
  }

  // Prevent multiple concurrent loads
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = (async () => {
    onProgress?.({ stage: 'loading', progress: 0 });

    // Load FFmpeg from CDN using dynamic import from URL
    // @ts-ignore - Dynamic import from URL works in browser
    const { FFmpeg } = await import(/* webpackIgnore: true */ 'https://esm.sh/@ffmpeg/ffmpeg@0.12.10');
    // @ts-ignore - Dynamic import from URL works in browser
    const { toBlobURL, fetchFile } = await import(/* webpackIgnore: true */ 'https://esm.sh/@ffmpeg/util@0.12.1');

    onProgress?.({ stage: 'loading', progress: 5 });

    ffmpeg = new FFmpeg();

    // Store fetchFile on the instance for later use
    ffmpeg._fetchFile = fetchFile;

    ffmpeg.on('progress', ({ progress }: { progress: number }) => {
      const percentage = Math.round(10 + progress * 80);
      onProgress?.({ stage: 'compressing', progress: percentage });
    });

    // Use local files from public/ffmpeg/ to avoid CORS and dependency issues
    const baseURL = window.location.origin + '/ffmpeg';

    console.log('[VideoCompression] Loading FFmpeg from:', baseURL);

    try {
      console.log('[VideoCompression] Fetching coreURL...');
      const coreURL = await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript');
      console.log('[VideoCompression] costURL generated:', coreURL);

      console.log('[VideoCompression] Fetching wasmURL...');
      const wasmURL = await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm');
      console.log('[VideoCompression] wasmURL generated:', wasmURL);

      // Use direct URL for worker so it can resolve relative imports (const.js, errors.js)
      const workerURL = `${baseURL}/ffmpeg-worker.js`;
      console.log('[VideoCompression] workerURL set to:', workerURL);

      console.log('[VideoCompression] Calling ffmpeg.load()...');
      await ffmpeg.load({
        coreURL,
        wasmURL,
        // For single-threaded 0.12.x, we need to override the class worker to avoid CDN load
        classWorkerURL: workerURL,
      });
      console.log('[VideoCompression] ffmpeg.load() completed');
    } catch (e) {
      console.error('[VideoCompression] Error loading FFmpeg:', e);
      throw e;
    }

    ffmpegLoaded = true;
    onProgress?.({ stage: 'loading', progress: 10 });

    return ffmpeg;
  })();

  return loadPromise;
}

/**
 * Compress video on web using FFmpeg.wasm
 * @param input - Either a blob URL string or a File/Blob object
 */
export async function compressVideo(
  input: string | File | Blob,
  onProgress?: ProgressCallback
): Promise<VideoCompressionResult> {
  onProgress?.({ stage: 'loading', progress: 0 });

  try {
    // If input is a string (blob URL), convert to Blob FIRST before loading FFmpeg
    // This prevents the blob URL from expiring while FFmpeg loads
    let inputBlob: Blob;
    if (typeof input === 'string') {
      console.log('[VideoCompression] Pre-fetching blob URL before FFmpeg load:', input);
      try {
        const response = await fetch(input);
        if (!response.ok) {
          throw new Error(`Fetch failed: ${response.status} ${response.statusText}`);
        }
        inputBlob = await response.blob();
        console.log('[VideoCompression] Pre-fetched blob, size:', inputBlob.size);
      } catch (fetchError) {
        console.error('[VideoCompression] Pre-fetch error details:', fetchError);
        throw new Error(`Failed to fetch input video: ${fetchError}`);
      }
    } else {
      inputBlob = input;
      console.log('[VideoCompression] Using provided File/Blob, size:', inputBlob.size);
    }

    // Skip compression for small files (under 5MB) - re-encoding can actually increase size
    const SKIP_COMPRESSION_THRESHOLD = 5 * 1024 * 1024; // 5MB
    if (inputBlob.size < SKIP_COMPRESSION_THRESHOLD) {
      console.log('[VideoCompression] File is small enough, skipping compression:', inputBlob.size, 'bytes');
      onProgress?.({ stage: 'finalizing', progress: 100 });

      // Return the original blob as-is
      const outputUri = URL.createObjectURL(inputBlob);
      return {
        uri: outputUri,
        size: inputBlob.size,
        duration: 0,
        mimeType: inputBlob.type || 'video/mp4',
      };
    }

    console.log('[VideoCompression] File is large, proceeding with compression...');

    // Now load FFmpeg (this can take a while)
    const ff = await loadFFmpeg(onProgress);

    // Convert blob to Uint8Array
    console.log('[VideoCompression] Converting blob to Uint8Array...');
    let inputData: Uint8Array;
    try {
      const buffer = await inputBlob.arrayBuffer();
      inputData = new Uint8Array(buffer);
      console.log('[VideoCompression] Input file ready, size:', inputData.byteLength);
    } catch (convertError) {
      console.error('[VideoCompression] Blob conversion error:', convertError);
      throw new Error(`Failed to convert input video: ${convertError}`);
    }

    await ff.writeFile('input.mp4', inputData);

    onProgress?.({ stage: 'compressing', progress: 15 });

    // Compress video with FFmpeg
    // Scale down to max 1080p while keeping aspect ratio
    console.log('[VideoCompression] Starting local ffmpeg compression...');
    await ff.exec([
      '-i', 'input.mp4',
      '-vf', `scale=min(${TARGET_MAX_RESOLUTION}\\,iw):min(${TARGET_MAX_RESOLUTION}\\,ih):force_original_aspect_ratio=decrease`,
      '-c:v', 'libx264',
      '-crf', '28',
      '-preset', 'ultrafast', // Use ultrafast for web to avoid hanging/timeout on slow devices
      '-c:a', 'aac',
      '-b:a', '128k',
      '-movflags', '+faststart',
      'output.mp4'
    ]);
    console.log('[VideoCompression] Compression finished execution');

    onProgress?.({ stage: 'finalizing', progress: 95 });

    const data = await ff.readFile('output.mp4');
    const blob = new Blob([data], { type: 'video/mp4' });
    const outputUri = URL.createObjectURL(blob);
    console.log('[VideoCompression] Output generated:', outputUri);

    try {
      await ff.deleteFile('input.mp4');
      await ff.deleteFile('output.mp4');
    } catch (e) {
      // Ignore delete errors
    }

    onProgress?.({ stage: 'finalizing', progress: 100 });

    return {
      uri: outputUri,
      size: blob.size,
      duration: 0,
      mimeType: 'video/mp4',
    };
  } catch (error) {
    console.error('[videoCompression.web] Error:', error);
    throw error; // Re-throw to see in UI
  }
}

/**
 * Get video metadata using HTML5 video element
 * @param input - Either a URL string or a File/Blob object
 */
export async function getVideoMetadata(input: string | File | Blob): Promise<{
  duration: number;
  width: number;
  height: number;
}> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';

    // Create object URL if input is a File/Blob
    const videoSrc = typeof input === 'string' ? input : URL.createObjectURL(input);
    const shouldRevokeUrl = typeof input !== 'string';

    video.onloadedmetadata = () => {
      resolve({
        duration: video.duration || 0,
        width: video.videoWidth || 0,
        height: video.videoHeight || 0,
      });
      if (shouldRevokeUrl) {
        URL.revokeObjectURL(videoSrc);
      }
    };

    video.onerror = () => {
      resolve({ duration: 0, width: 0, height: 0 });
      if (shouldRevokeUrl) {
        URL.revokeObjectURL(videoSrc);
      }
    };

    video.src = videoSrc;
  });
}
